import Locale from './locale'

const DOMAIN = 'termly.io'

// NOTE: These values are not aribtrary...they correspond to the `type` field
// that we get from the backend.
const ACCEPTABLE_USE_POLICY = 'AcceptableUsePolicy'
const ACCESSIBILITY_STATEMENT = 'AccessibilityStatement'
const COOKIE = 'Cookie'
const DISCLAIMER = 'Disclaimer'
const EULA = 'EULA'
const IMPRESSUM = 'Impressum'
const PRIVACY = 'Privacy'
const RETURN = 'Return'
const SHIPPING_POLICY = 'Shipping'
const TERMS = 'Terms'

const UNEDITABLE_POLICY_TYPES = new Set([
  COOKIE
])

/* eslint-disable global-require */
const POLICY_CONFIGS = {
  [ACCEPTABLE_USE_POLICY]: {
    trackingEventName: 'hasAcceptableUsePolicy',
    generatorLink: makeGeneratorLink('acceptable-use-policy-generator'),
    icon: require('./img/acceptable-use-icon.svg'),
    id: 'acceptableUsePolicy',
    name: Locale.acceptableUsePolicy,
    priority: 7,
    slug: 'acceptable-use',
    tip: Locale.acceptableUsePolicyTip,
  },
  [COOKIE]: {
    trackingEventName: void 0,
    generatorLink: makeGeneratorLink('cookie-consent-manager'),
    icon: require('./img/cookie-icon.svg'),
    id: 'cookiePolicy',
    name: Locale.cookiePolicy,
    priority: 2,
    slug: 'cookie-policy',
    tip: Locale.cookiePolicyTip,
  },
  [DISCLAIMER]: {
    trackingEventName: 'hasPublishedDisclaimer',
    generatorLink: makeGeneratorLink('disclaimer-generator'),
    icon: require('./img/disclaimer-icon.svg'),
    id: 'disclaimer',
    name: Locale.disclaimer,
    priority: 5,
    slug: 'disclaimer',
    tip: Locale.disclaimerTip,
  },
  [EULA]: {
    trackingEventName: 'hasEULA',
    generatorLink: makeGeneratorLink('eula-generator'),
    icon: require('./img/eula-icon.svg'),
    id: 'eula',
    name: Locale.eula,
    priority: 3,
    slug: 'eula',
    tip: Locale.eulaTip,
  },
  [IMPRESSUM]: {
    trackingEventName: 'hasImpressum',
    generatorLink: makeGeneratorLink('impressum-generator'),
    icon: require('./img/impressum-icon.svg'),
    id: 'impressum',
    name: Locale.impressum,
    priority: 8,
    slug: 'impressum',
    tip: Locale.impressumTip,
  },
  [ACCESSIBILITY_STATEMENT]: {
    trackingEventName: 'a11y-statement',
    generatorLink: makeGeneratorLink('accessibility-statement-generator'),
    icon: require('./img/accessibility-statement-icon.svg'),
    id: 'accessibilityStatement',
    name: Locale.accessibilityStatement,
    priority: 9,
    slug: 'a11y-statement',
    tip: Locale.accessibilityStatementTip,
  },
  [PRIVACY]: {
    trackingEventName: 'hasPublishedPrivacyPolicy',
    generatorLink: makeGeneratorLink('privacy-policy-generator'),
    icon: require('./img/privacy-icon.svg'),
    id: 'privacyPolicy',
    name: Locale.privacyPolicy,
    priority: 1,
    slug: 'privacy-policy',
    tip: Locale.privacyPolicyTip,
  },
  [RETURN]: {
    trackingEventName: 'hasPublishedReturnPolicy',
    generatorLink: makeGeneratorLink('refund-return-policy-generator'),
    icon: require('./img/return-icon.svg'),
    id: 'refundPolicy',
    name: Locale.returnPolicy,
    priority: 4,
    slug: 'refund-policy',
    tip: Locale.returnPolicyTip,
  },
  [SHIPPING_POLICY]: {
    trackingEventName: 'hasPublishedShippingPolicy',
    generatorLink: makeGeneratorLink('shipping-policy-generator'),
    icon: require('./img/shipping-policy-icon.svg'),
    id: 'shippingPolicy',
    name: Locale.shippingPolicy,
    priority: 6,
    slug: 'shipping-policy',
    tip: Locale.shippingPolicyTip,
  },
  [TERMS]: {
    trackingEventName: 'hasPublishedTermsofUse',
    generatorLink: makeGeneratorLink('terms-and-conditions-generator'),
    icon: require('./img/terms-icon.svg'),
    id: 'termsOfService',
    name: Locale.termsAndConditions,
    priority: 2,
    slug: 'terms-of-service',
    tip: Locale.termsAndConditionsTip,
  },
}
/* eslint-enable */

const ALL_TYPES = Object.keys(POLICY_CONFIGS)

export default {
  ACCEPTABLE_USE_POLICY,
  ACCESSIBILITY_STATEMENT,
  COOKIE,
  DISCLAIMER,
  EULA,
  IMPRESSUM,
  PRIVACY,
  RETURN,
  SHIPPING_POLICY,
  TERMS,

  get allTypes() {
    return ALL_TYPES
  },
  get defaultPolicy() {
    return PRIVACY
  },

  hasCustomizedPolicyEntryPoint(policyType) {
    return policyType === COOKIE
  },

  hasPolicyMigratorFunction(policyType) {
    return policyType !== COOKIE
  },

  isEditablePolicyType(policyType) {
    if ( !policyType ) {
      return false
    }

    return !UNEDITABLE_POLICY_TYPES.has(policyType)
  },

  getSlugForPolicyType(policyType) {
    return POLICY_CONFIGS[policyType]?.slug
  },

  getPolicyTypeForSlug(desiredSlug) {
    if ( !this.typeToSlug ) {
      this.typeToSlug = Object.entries(POLICY_CONFIGS)
        .reduce((collector, [policyType, { slug }]) => ({
          ...collector,
          [slug]: policyType,
        }), {})
    }

    return this.typeToSlug[desiredSlug]
  },

  getPriorityForPolicyType(policyType) {
    return POLICY_CONFIGS[policyType]?.priority
  },

  getAllTypesByPriority() {
    return ALL_TYPES.sort(compareByPriority)
  },

  getTrackingEventNameForPolicyType(policyType) {
    return POLICY_CONFIGS[policyType]?.trackingEventName
  },

  getGeneratorLink(policyType) {
    return POLICY_CONFIGS[policyType]?.generatorLink
  },

  getIconSrc(policyType) {
    return POLICY_CONFIGS[policyType]?.icon
  },

  getID(policyType) {
    return POLICY_CONFIGS[policyType]?.id
  },

  getNameMessage(policyType) {
    return POLICY_CONFIGS[policyType]?.name
  },

  getTipMessage(policyType) {
    return POLICY_CONFIGS[policyType]?.tip
  }
}

function compareByPriority(firstPolicyType, secondPolicyType) {
  return POLICY_CONFIGS[firstPolicyType].priority - POLICY_CONFIGS[secondPolicyType].priority
}

function makeGeneratorLink(slug) {
  return `https://${ DOMAIN }/products/${ slug }/`
}

export const _private = {
  POLICY_CONFIGS,
}
